import * as XLSXStyle from 'xlsx-js-style';
import { isoADisplayDDMMAAAA } from './fechaReporte';
import {
  groupPopularidadInDisplayOrder,
  mergePopularidadEncabezado,
  normalizePopularidadRow,
  POPULARIDAD_COLUMN_LABELS,
  sumGroupTotals,
} from './popularidadReportModel';
import { appendFiltrosSucursalesCajasExcel } from './reporteFiltrosEncabezadoExport';

const fmtMoneyCell = (n) =>
  n == null || Number.isNaN(n) ? '' : Number(n);

const formatFecha = (iso) =>
  iso ? isoADisplayDDMMAAAA(String(iso).split('T')[0]) : '—';

/** Columnas de tabla (A–H): mismo orden que {@link POPULARIDAD_COLUMN_LABELS}. */
const POP_TABLE_COLS = 8;

const BORDER_GRID = { style: 'thin', color: { rgb: 'FFCBD5E1' } };
const BORDER_HEAD = { style: 'thin', color: { rgb: 'FF000000' } };
const BORDER_SUBTOP = { style: 'thin', color: { rgb: 'FF475569' } };

/** Anchos para “cuadrar” la tabla como la vista impresa. */
const POP_COL_WCH = [11, 44, 11, 12, 12, 12, 14, 14];

function rowMatchesColumnLabels(row) {
  return (
    Array.isArray(row) &&
    row[0] === POPULARIDAD_COLUMN_LABELS[0] &&
    row[1] === POPULARIDAD_COLUMN_LABELS[1]
  );
}

/**
 * Fila de título de grupo: solo texto en A, debajo de la cabecera Código/Nombre/… (no confundir con líneas del encabezado impreso).
 */
function isFamiliaBannerRow(row, rowIndex, columnHeaderRowIdx) {
  if (!Array.isArray(row)) return false;
  if (columnHeaderRowIdx < 0 || rowIndex <= columnHeaderRowIdx) return false;
  if (rowMatchesColumnLabels(row)) return false;
  const label = row[0];
  if (label == null || String(label).trim() === '') return false;
  for (let c = 1; c < POP_TABLE_COLS; c++) {
    const x = row[c];
    if (x !== '' && x != null && x !== undefined) return false;
  }
  const t = String(label);
  if (/^\s*Total\b/i.test(t)) return false;
  if (t === 'Subtotal') return false;
  if (/impuesto.*bebidas/i.test(t)) return false;
  return true;
}

function isTotalLabelRow(row) {
  return typeof row?.[0] === 'string' && /^\s*Total\b/i.test(row[0]);
}

function isImpuestoBEBRow(row) {
  return typeof row?.[0] === 'string' && /impuesto.*bebidas/i.test(row[0]);
}

function isSubtotalFooterRow(row) {
  return row?.[0] === 'Subtotal';
}

function ensureCell(ws, r, c) {
  const addr = XLSXStyle.utils.encode_cell({ r, c });
  if (!ws[addr]) ws[addr] = { t: 's', v: '' };
  return ws[addr];
}

function applyNumberFormat(cell) {
  if (cell && typeof cell.v === 'number' && Number.isFinite(cell.v)) {
    cell.z = '#,##0.00';
  }
}

/** Primera línea “corporativa” del encabezado (sucursal o nombre comercial), antes de RFC/título/filtros. */
function indexFirstEncCorporateLine(aoa, firstFamRow) {
  for (let i = 0; i < firstFamRow; i++) {
    const rw = aoa[i];
    if (!rw?.length || !rw.some((x) => x !== '' && x != null)) continue;
    if (rowMatchesColumnLabels(rw)) continue;
    const tx = String(rw[0] ?? '');
    if (/^RFC:/i.test(tx) || /^Del:/i.test(tx) || /^Cajas:/i.test(tx) || /^Turnos:/i.test(tx))
      continue;
    if (/REPORTE\s+POPULARIDAD/i.test(tx) || /INCLUYENDO\s+CORTES/i.test(tx)) continue;
    return i;
  }
  return -1;
}

/**
 * Encabezado tipo impreso (centrado); familias a la izquierda; cabecera de tabla oscura;
 * datos alineados; totales con borde superior en columnas de métricas.
 */
function stylePopularidadSheet(ws, aoa) {
  const merges = [];

  const columnHeaderRowIdx = aoa.findIndex(rowMatchesColumnLabels);
  /* Detectar número real de columnas de la cabecera de tabla */
  const nCols = columnHeaderRowIdx >= 0
    ? (aoa[columnHeaderRowIdx]?.length || POP_TABLE_COLS)
    : POP_TABLE_COLS;

  const maxCol = Math.max(
    nCols - 1,
    aoa.reduce((m, row) => Math.max(m, (row?.length || 0) - 1), 0)
  );

  const firstFamiliaRow = aoa.findIndex((row, idx) =>
    isFamiliaBannerRow(row, idx, columnHeaderRowIdx)
  );
  const idxFirstCorporate =
    firstFamiliaRow >= 0 ? indexFirstEncCorporateLine(aoa, firstFamiliaRow) : -1;

  // Sin bloque familia: mensaje u hoja mínima — centrar primera celda útil
  if (firstFamiliaRow < 0) {
    const idx = aoa.findIndex((row) => row?.length && row.some((x) => x !== '' && x != null));
    if (idx >= 0) {
      merges.push({ s: { r: idx, c: 0 }, e: { r: idx, c: Math.max(maxCol, nCols - 1) } });
      const cell = ensureCell(ws, idx, 0);
      cell.s = {
        alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
        font: { color: { rgb: 'FF64748B' } },
      };
    }
    ws['!merges'] = merges;
    return;
  }

  // Bloque encabezado impreso: fusionado A–H, centrado (empresa, RFC, título, filtros)
  for (let r = 0; r < firstFamiliaRow; r++) {
    const row = aoa[r];
    if (!row || !row.some((x) => x !== '' && x != null)) continue;
    if (rowMatchesColumnLabels(row)) continue;
    merges.push({ s: { r, c: 0 }, e: { r, c: nCols - 1 } });
    const cell = ensureCell(ws, r, 0);
    const t = String(cell.v ?? '');
    const tituloRep = /REPORTE\s+POPULARIDAD/i.test(t);
    const cortesias = /INCLUYENDO\s+CORTES/i.test(t);
    const primeraCorp = idxFirstCorporate >= 0 && r === idxFirstCorporate;
    const bold = tituloRep || cortesias || primeraCorp;
    cell.s = {
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      font: {
        bold,
        sz: tituloRep ? 11 : 10,
        color: { rgb: 'FF0F172A' },
      },
    };
  }

  for (let r = 0; r < aoa.length; r++) {
    const row = aoa[r] || [];

    if (isFamiliaBannerRow(row, r, columnHeaderRowIdx)) {
      merges.push({ s: { r, c: 0 }, e: { r, c: nCols - 1 } });
      for (let c = 0; c < nCols; c++) {
        const cell = ensureCell(ws, r, c);
        cell.s = {
          font: { bold: true, sz: 11 },
          alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
          fill: { fgColor: { rgb: 'FFE2E8F0' } },
        };
      }
      continue;
    }

    if (rowMatchesColumnLabels(row)) {
      for (let c = 0; c < nCols; c++) {
        const cell = ensureCell(ws, r, c);
        const align = c <= 1 ? 'left' : 'right';
        cell.s = {
          font: { bold: true, color: { rgb: 'FFFFFFFF' } },
          alignment: { horizontal: align, vertical: 'center', wrapText: true },
          fill: { fgColor: { rgb: 'FF1E293B' } },
          border: {
            top: BORDER_HEAD,
            bottom: BORDER_HEAD,
            left: c === 0 ? BORDER_HEAD : BORDER_GRID,
            right: c === nCols - 1 ? BORDER_HEAD : BORDER_GRID,
          },
        };
      }
      continue;
    }

    if (isTotalLabelRow(row)) {
      /* Cols métricas con borde superior: cantidad(2), tCosto(4), importe(nCols-2), utilidad(nCols-1) */
      const metricColsTop = new Set([2, 4, nCols - 2, nCols - 1]);
      for (let c = 0; c < nCols; c++) {
        const cell = ensureCell(ws, r, c);
        applyNumberFormat(cell);
        const borderTop = metricColsTop.has(c) ? BORDER_SUBTOP : undefined;
        cell.s = {
          font: { bold: true, color: { rgb: 'FF0F172A' } },
          alignment: {
            horizontal: c <= 1 ? 'left' : 'right',
            vertical: 'center',
          },
          ...(borderTop ? { border: { top: borderTop } } : {}),
        };
      }
      continue;
    }

    if (isImpuestoBEBRow(row)) {
      for (let c = 0; c < nCols; c++) {
        const cell = ensureCell(ws, r, c);
        applyNumberFormat(cell);
        cell.s = {
          alignment: {
            horizontal: c === 0 ? 'left' : c >= nCols - 1 ? 'right' : 'center',
            vertical: 'center',
          },
          font: { bold: c === 0, color: { rgb: 'FF0F172A' } },
        };
      }
      continue;
    }

    if (isSubtotalFooterRow(row)) {
      for (let c = 0; c < nCols; c++) {
        const cell = ensureCell(ws, r, c);
        applyNumberFormat(cell);
        cell.s = {
          font: { bold: true },
          alignment: {
            horizontal: c === 0 ? 'left' : c === nCols - 2 ? 'right' : 'center',
            vertical: 'center',
          },
        };
      }
      continue;
    }

    // Filas de datos (producto): primera celda no es token especial
    if (
      row.length >= nCols &&
      !isFamiliaBannerRow(row, r, columnHeaderRowIdx) &&
      !rowMatchesColumnLabels(row)
    ) {
      for (let c = 0; c < nCols; c++) {
        const cell = ensureCell(ws, r, c);
        applyNumberFormat(cell);
        const isTextCol = c <= 1;
        cell.s = {
          alignment: {
            horizontal: isTextCol ? 'left' : 'right',
            vertical: 'center',
            wrapText: isTextCol,
          },
          font: { color: { rgb: 'FF0F172A' } },
          border: {
            top: BORDER_GRID,
            bottom: BORDER_GRID,
            left: BORDER_GRID,
            right: BORDER_GRID,
          },
        };
      }
    }
  }

  ws['!merges'] = merges;
}

/** Primera línea del encabezado: sucursal en mayúsculas si no viene ya en nombre comercial (misma regla que la vista/PDF). */
function appendSucursalEncabezadoLine(aoa, sucursalNombre, nombreComercialEnc) {
  const up = (s) => String(s || '').trim().toUpperCase();
  const sn = String(sucursalNombre || '').trim();
  if (!sn) return;
  const nc = up(nombreComercialEnc || '');
  if (nc.includes(sn.toUpperCase())) return;
  aoa.push([sn.toUpperCase()]);
}

/**
 * Encabezado tipo documento / captura de referencia: líneas separadas, orden empresa → RFC → título → filtros.
 */
function appendEncabezadoExcelTipoImpreso(aoa, encMerged, form, sucursalNombre) {
  const up = (s) => String(s || '').trim().toUpperCase();
  if (
    encMerged.bloqueTexto &&
    String(encMerged.bloqueTexto).trim() &&
    !encMerged.nombreComercial &&
    !encMerged.reporteTitulo
  ) {
    appendSucursalEncabezadoLine(aoa, sucursalNombre, encMerged.nombreComercial);
    for (const line of String(encMerged.bloqueTexto).split('\n')) {
      const t = line.trim();
      if (t) aoa.push([t]);
    }
    aoa.push([]);
    return;
  }

  appendSucursalEncabezadoLine(aoa, sucursalNombre, encMerged.nombreComercial);

  if (encMerged.nombreComercial) {
    aoa.push([up(encMerged.nombreComercial)]);
  }

  if (encMerged.rfc) {
    aoa.push([up(`RFC: ${encMerged.rfc}`)]);
  }

  aoa.push([]);

  if (encMerged.reporteTitulo) {
    aoa.push([up(encMerged.reporteTitulo)]);
  } else {
    aoa.push(['REPORTE POPULARIDAD DE PRODUCTOS']);
  }
  if (encMerged.cortesias) {
    aoa.push([up(encMerged.cortesias)]);
  }

  aoa.push([]);

  if (encMerged.periodoTexto) {
    aoa.push([String(encMerged.periodoTexto).trim()]);
  } else {
    aoa.push([
      `Del: ${formatFecha(form.fechaInicio)} al ${formatFecha(form.fechaFin)}`,
    ]);
  }
  appendFiltrosSucursalesCajasExcel(aoa, encMerged, form);
  if (encMerged.turnosTexto) {
    aoa.push([`Turnos: ${encMerged.turnosTexto}`]);
  } else {
    aoa.push([`Turnos: ${form.turnoInicio ?? '—'} a ${form.turnoFin ?? '—'}`]);
  }

  aoa.push([]);
}

/**
 * Excel por familia con una sola fila de cabecera de columnas (Código, Nombre, …), subtotales y total global.
 * @param {object[]} rawRows
 * @param {{ encabezado?: object|null, encabezadoFallback?: object, sucursalNombre?: string, reporteTitulo?: string, incluirCortesias?: boolean, bebidasAlcoholicas?: boolean, fechaInicio?: string, fechaFin?: string, cajaInicio?: number, cajaFin?: number, turnoInicio?: number, turnoFin?: number, footer?: { subtotal?: number|null } }} [options]
 */
export function buildPopularidadExcelAoa(rawRows, options = {}) {
  const {
    encabezado: encApi,
    encabezadoFallback,
    sucursalNombre = '',
    reporteTitulo = 'REPORTE POPULARIDAD DE PRODUCTOS',
    incluirCortesias = false,
    bebidasAlcoholicas = false,
    incluirPrecioMenu = true,
    fechaInicio = '',
    fechaFin = '',
    cajaInicio,
    cajaFin,
    turnoInicio,
    turnoFin,
    footer = {},
  } = options;

  const form = { fechaInicio, fechaFin, cajaInicio, cajaFin, turnoInicio, turnoFin };
  const encMerged = mergePopularidadEncabezado(encApi, encabezadoFallback, {
    reporteTitulo,
    incluirCortesias,
    bebidasAlcoholicas,
  });

  const aoa = [];
  appendEncabezadoExcelTipoImpreso(aoa, encMerged, form, sucursalNombre);

  if (!Array.isArray(rawRows) || rawRows.length === 0) {
    aoa.push(['Sin datos para el rango seleccionado.']);
    return aoa;
  }

  const normalized = rawRows.map(normalizePopularidadRow).filter(Boolean);
  if (!normalized.length) {
    aoa.push(['Sin datos normalizables para exportar.']);
    return aoa;
  }

  const groups = groupPopularidadInDisplayOrder(normalized);

  const colLabels = incluirPrecioMenu
    ? [...POPULARIDAD_COLUMN_LABELS]
    : POPULARIDAD_COLUMN_LABELS.filter((h) => h !== 'Precio');
  aoa.push(colLabels);

  const nCols = colLabels.length;
  const emptyRow = () => Array(nCols).fill('');

  for (const [familia, items] of groups) {
    aoa.push([String(familia).trim().toUpperCase(), ...Array(nCols - 1).fill('')]);
    for (const r of items) {
      const row = [
        r.codigo,
        r.nombre,
        r.cantidad,
        fmtMoneyCell(r.costo),
        fmtMoneyCell(r.tCosto),
      ];
      if (incluirPrecioMenu) row.push(fmtMoneyCell(r.precio));
      row.push(fmtMoneyCell(r.importe), fmtMoneyCell(r.utilidad));
      aoa.push(row);
    }
    const sub = sumGroupTotals(items);
    const subRow = [`Total ${familia}`, '', sub.cantidad, '', sub.tCosto];
    if (incluirPrecioMenu) subRow.push('');
    subRow.push(sub.importe, sub.utilidad);
    aoa.push(subRow);
    aoa.push(emptyRow());
  }

  const grand = sumGroupTotals(normalized);
  const grandRow = ['Total Global', '', grand.cantidad, '', grand.tCosto];
  if (incluirPrecioMenu) grandRow.push('');
  grandRow.push(grand.importe, grand.utilidad);
  aoa.push(grandRow);

  if (bebidasAlcoholicas && grand.impuestoBA != null && grand.impuestoBA !== 0) {
    const impRow = emptyRow();
    impRow[0] = 'Impuesto bebidas alcohólicas';
    impRow[nCols - 1] = grand.impuestoBA;
    aoa.push([]);
    aoa.push(impRow);
  }
  const subtotal = Number(footer?.subtotal);
  if (bebidasAlcoholicas && Number.isFinite(subtotal)) {
    const subTotalRow = emptyRow();
    subTotalRow[0] = 'Subtotal';
    subTotalRow[nCols - 2] = subtotal;
    aoa.push([]);
    aoa.push(subTotalRow);
  }

  return aoa;
}

/**
 * Libro .xlsx listo para escribir; layout alineado al PDF (encabezado centrado, tabla cuadriculada).
 */
export function buildPopularidadExcelWorkbook(rawRows, options, sheetName) {
  const incluirPrecioMenu = options?.incluirPrecioMenu !== false;
  const aoa = buildPopularidadExcelAoa(rawRows, options);
  const ws = XLSXStyle.utils.aoa_to_sheet(aoa);
  stylePopularidadSheet(ws, aoa);
  /* Anchos: quitar la columna Precio (índice 5) cuando no se incluye */
  const colWidths = incluirPrecioMenu
    ? POP_COL_WCH
    : [...POP_COL_WCH.slice(0, 5), ...POP_COL_WCH.slice(6)];
  const colCount = Math.max(
    colWidths.length,
    aoa.reduce((m, row) => Math.max(m, row.length), 0)
  );
  ws['!cols'] = Array.from({ length: colCount }, (_, i) => ({
    wch: colWidths[i] ?? 12,
  }));
  const wb = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(wb, ws, sheetName);
  return wb;
}

/** Genera y descarga el .xlsx de popularidad con formato y alineación tipo impreso. */
export function downloadPopularidadExcelFile(rawRows, options, fileName, sheetName) {
  const wb = buildPopularidadExcelWorkbook(rawRows, options, sheetName);
  XLSXStyle.writeFile(wb, fileName);
}
